import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import _ from "lodash";
import cx from "classnames";
import { Form } from "react-bootstrap";

import { EditorContext } from "components/Editor";
import { updateWidgetProperty } from "actions/editor";
import { Font } from "components/Editor/components/properties/fonts";
import { Fonts } from '@paperflite-marketing/cleverstory-commons';
import { Property } from "components/Editor/components/properties";

import { associateFontsWithWigdet } from "actions/fonts";

import styles from "./properties.module.scss";

export const FontBackgroundSettings = React.memo(
  ({ id, widget, breakpoint, modal = false, handlePropertyChange }) => {
    const dispatch = useDispatch();

    const editorContext = React.useContext(EditorContext);
    const editorSession = editorContext.editorSession.id;

    const deviceProperties = _.get(widget, `properties.${breakpoint}`, {});
    const fontStyles = _.get(deviceProperties, "font", {});
    const fontFamily = _.get(deviceProperties, 'font.fontFamily', 'Montserrat,sans-serif');
    const primaryColor = _.get(fontStyles, "color", "#000000");
    const secondaryColor = _.get(fontStyles, "secondaryColor", "#000000");
    const tertiaryColor = _.get(fontStyles, "tertiaryColor", "#000000");
    const fontSize = _.get(fontStyles, "fontSize", 16);
    
    Fonts.useFont(_.get(widget, 'fonts') || []);

    const handleFontStylesChange = (type, value) => {

      handlePropertyChange("font", {
        ...fontStyles,
        [type]: value,
      });
    };

    const handleFontFamily = (value) => {
      var font = _.get(value, "font.id");
      var family = _.get(value, "font.fontFamily");
      if (!family) {
        return;
      }

      var request = {
        ...fontStyles,
        fontFamily: family,
      };

      handlePropertyChange("font", request);
      if (font) {
        dispatch(associateFontsWithWigdet(editorSession, id, [font]));
      }
    };

    return (
      <>
        {(breakpoint === "desktop" || modal) && (
          <Font.Family value={fontFamily}
          onChange={handleFontFamily} 
          className={styles.buttonStyleProperty}/>
        )}
        <Property name="Font Size">
          <Property.Slider
            min={10}
            max={16}
            step={1}
            input
            value={fontSize}
            onChange={(value) => {
              handleFontStylesChange("fontSize", value);
            }}
          />
        </Property>
        {(breakpoint === "desktop" || modal) && (
          <>
            <Property.Color
              title={"Primary Font Color"}
              value={primaryColor}
              onChange={(color) => {
                handleFontStylesChange("color", color);
              }}
            />
            <Property.Color
              title={"Secondary Font Color"}
              value={secondaryColor}
              onChange={(color) => {
                handleFontStylesChange("secondaryColor", color);
              }}
            />
            <Property.Color
              title={"Placeholder Font Color"}
              value={tertiaryColor}
              onChange={(color) => {
                handleFontStylesChange("tertiaryColor", color);
              }}
            />
          </>
        )}
      </>
    );
  }
);

export const SeekLayoutSettings = React.memo(({
  id,
  modal = false,
  breakpoint,
  handlePropertyChange,
  widget,
}) => {
  const questionDesign = _.get(
    widget,
    `properties.${breakpoint}.questionDesign`,
    {}
  );
  const layoutType = _.get(questionDesign, `type`, "bar");

  return (
    <div className={styles.seekLayoutSettings}>
      {/* {modal && (
        <>
          <span className={styles.titleBar}>Question Style</span>
          <div className={styles.questionStyleWrapper}>
            <div className={cx(styles.boxContainer)}>
              <div
                className={cx(styles.tile, {
                  [styles.active]: layoutType == "bar",
                })}
              ></div>
              <span className={styles.label}>Bar</span>
            </div>
            <div className={cx(styles.boxContainer)}>
              <div
                className={cx(styles.tile, {
                  [styles.active]: layoutType == "button",
                })}
              ></div>
              <span className={styles.label}>Button</span>
            </div>
          </div>
        </>
      )} */}
      {layoutType === "bar" && (
        <BarStyleSettings
          id={id}
          widget={widget}
          breakpoint={breakpoint}
          handlePropertyChange={handlePropertyChange}
        />
      )}
      {layoutType === "button" && (
        <ButtonStyleSettings
          id={id}
          widget={widget}
          breakpoint={breakpoint}
          handlePropertyChange={handlePropertyChange}
        />
      )}
    </div>
  );
});

export const BarStyleSettings = React.memo(({
  id,
  widget,
  breakpoint,
  handlePropertyChange,
}) => {
  const questionDesign = _.get(
    widget,
    `properties.${breakpoint}.questionDesign`,
    {}
  );
  const barProperties = _.get(questionDesign, `bar`, {});

  const inputFieldColor = _.get(barProperties, `inputFieldColor`, {
    r: 255,
    g: 255,
    b: 255,
    a: 1,
  });

  const promptEnabled = _.get(barProperties, `promptEnabled`, false);
  const prompts = _.get(barProperties, `prompts`, []);

  const border = _.get(barProperties, `border`, {});
  const borderColor = _.get(border, "color", null);
  const borderStyle = _.get(border, "style", "bottom");
  const borderWidth = _.get(border, "width", 0);
  const borderRadius = _.get(border, "radius", {
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0,
  });
  const placeholderQuestion = _.get(
    barProperties,
    `placeholderQuestion`,
    "Enter your message here"
  );

  const handleBackgroundColorChange = (color) => {
    handlePropertyChange("questionDesign", {
      ...questionDesign,
      bar: {
        ...barProperties,
        backgroundColor: color,
      },
    });
  };

  const handleInputColorChange = (color) => {
    handlePropertyChange("questionDesign", {
      ...questionDesign,
      bar: {
        ...barProperties,
        inputFieldColor: color,
      },
    });
  };

  const handleBorderStyles = (key, value) => {
    handlePropertyChange("questionDesign", {
      ...questionDesign,
      bar: {
        ...barProperties,
        border: {
          ...border,
          [key]: value,
        },
      },
    });
  };

  const togglePrompts = () => {
    handlePropertyChange("questionDesign", {
      ...questionDesign,
      bar: {
        ...barProperties,
        promptEnabled: !promptEnabled,
      },
    });
  };

  const handlePlaceholderChange = (e) => {
    handlePropertyChange("questionDesign", {
      ...questionDesign,
      bar: {
        ...barProperties,
        placeholderQuestion: e.target.value,
      },
    });
  };

  const handlePromptChange = (index, value) => {
    const updatedPrompts = [...prompts];
    updatedPrompts[index] = value;

    handlePropertyChange("questionDesign", {
      ...questionDesign,
      bar: {
        ...barProperties,
        prompts: updatedPrompts,
      },
    });
  };

  return (
    <div className={styles.barCustomizationWrapper}>
      <span className={styles.titleBar}>Bar Appearance</span>
      <Property
        name="Placeholder Question"
        className={styles.placeholderQuestion}
      >
        <Property.Input
          value={placeholderQuestion}
          onChange={handlePlaceholderChange}
          placeholder="Enter your message here"
        />
      </Property>
      <Property name="Corner Radius" className={styles.buttonStyleProperty}>
        <Property.BorderRadius
          value={borderRadius}
          onChange={(value) => {
            handleBorderStyles("radius", value);
          }}
        />
      </Property>
      <Property.Color
        title={"Input Field Colour"}
        clearable={true}
        className={styles.inputFieldColorProperty}
        value={inputFieldColor}
        onChange={handleInputColorChange}
      />
      <Property name="Border Type" className={styles.borderTypeProperty}>
        <div key={`inline-radio`} className={styles.checkBoxwrapper}>
          <Form.Check
            inline
            label="None"
            className={styles.radioButton}
            checked={borderStyle === "none"}
            onMouseDown={() => handleBorderStyles("style", "none")}
            name="none"
            type="radio"
            id={`inline-radio-1`}
          />
          <Form.Check
            inline
            label="All Sides"
            className={styles.radioButton}
            checked={borderStyle === "all"}
            onMouseDown={() => handleBorderStyles("style", "all")}
            name="all"
            type="radio"
            id={`inline-radio-2`}
          />
          <Form.Check
            inline
            name="bottom"
            label="Bottom"
            className={styles.radioButton}
            checked={borderStyle === "bottom"}
            onMouseDown={() => handleBorderStyles("style", "bottom")}
            type="radio"
            id={`inline-radio-3`}
          />
        </div>
      </Property>
      <Property.Color
        title={"Border Colour"}
        className={styles.borderColorProperty}
        clearable={true}
        gradient={false}
        value={borderColor}
        onChange={(color) => {
          handleBorderStyles("color", color);
        }}
      />
      <Property className={styles.borderWidth} name="Stroke Width">
        <Property.Slider
          min={0}
          max={10}
          step={1}
          input
          value={borderWidth}
          onChange={(value) => {
            handleBorderStyles("width", value);
          }}
        />
      </Property>
      <div className={styles.promptEnabled}>
        <Property.Switch
          title={"Prompts"}
          className={styles.toggleSwitch}
          value={promptEnabled}
          onChange={togglePrompts}
        />
        {promptEnabled && (
          <div className={styles.promptList}>
            {_.map(prompts, (data, index) => {
              return (
                <Form.Control
                  key={index}
                  type="text"
                  className={styles.promptQuestion}
                  value={data}
                  onChange={(e) => {
                    handlePromptChange(index, e.target.value);
                  }}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
});

const ButtonStyleSettings = ({ id, widget, onCancel }) => {
  return <div className={styles.buttonCustomizationWrapper}></div>;
};

export const ResponseDesignSettings = React.memo(({
  id,
  widget,
  breakpoint,
  handlePropertyChange,
}) => {
  const responseDesign = _.get(
    widget,
    `properties.${breakpoint}.responseDesign`,
    {}
  );
  const type = _.get(responseDesign, `type`, "bar");
  const backgroundColor = _.get(responseDesign, `backgroundColor`);
  const accentColor = _.get(responseDesign, `accentColor`);
  const processingMessage = _.get(
    responseDesign,
    `processingMessage`,
    "Processing..."
  );
  const noResultsMessage = _.get(
    responseDesign,
    `noResultsMessage`,
    "No results found"
  );

  const responseLayoutOption = [
    {
      id: 1,
      label: "Inline",
      value: "inline",
      icon: "/images/palette/seek/layout/inline.png",
    },
    {
      id: 2,
      label: "Pop-Up",
      value: "popup",
      icon: "/images/palette/seek/layout/popUp.png",
    },
    {
      id: 3,
      label: "Slider",
      value: "slider",
      icon: "/images/palette/seek/layout/slider.png",
    },
  ];

  const handleBackgroundColorChange = (color) => {
    handlePropertyChange("responseDesign", {
      ...responseDesign,
      backgroundColor: color,
    });
  };

  const handleAccentColorChange = (color) => {
    handlePropertyChange("responseDesign", {
      ...responseDesign,
      accentColor: color,
    });
  };

  const handleResponseStyle = (type) => {
    handlePropertyChange("responseDesign", {
      ...responseDesign,
      type: type,
    });
  };

  const handleMessageChange = (value, type) => {
    handlePropertyChange("responseDesign", {
      ...responseDesign,
      [type]: value,
    });
  };

  return (
    <div className={styles.responsiveDesignSettings}>
      {breakpoint === "desktop" && (
        <div className={styles.modalStyleList}>
          {_.map(responseLayoutOption, (option, index) => {
            return (
              <div
                key={index}
                onClick={() => handleResponseStyle(option.value)}
                className={cx(styles.option, {
                  [styles.active]: option.value == type,
                })}
              >
                <img className={styles.iconImg} src={option.icon} />
                <span className={styles.label}>{option.label}</span>
              </div>
            );
          })}
        </div>
      )}
      <Property.Color
        title={"Background Colour"}
        value={backgroundColor}
        onChange={handleBackgroundColorChange}
      />
      <Property.Color
        title={"Accent Colour"}
        clearable={true}
        className={styles.accentColorProperty}
        value={accentColor}
        onChange={handleAccentColorChange}
      />

      <Property className={styles.messageContainer} name="Processing Message">
        <Form.Control
          type="text"
          as="textarea"
          className={styles.processMessage}
          onChange={(e) =>
            handleMessageChange(e.target.value, "processingMessage")
          }
          value={processingMessage}
        />
      </Property>
    </div>
  );
});
